import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { API_URL, USER_POOL_ID, USER_POOL_CLIENT_ID } from '../config';

const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: USER_POOL_CLIENT_ID,
});

function getCurrentSession() {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) return reject('No user');
    cognitoUser.getSession((err, session) => {
      if (err) return reject(err);
      resolve(session);
    });
  });
}

const TOOLBAR_BUTTONS = [
  { key: 'h1',       label: 'H1',         wrap: { before: '# ', after: '' },          block: true },
  { key: 'h2',       label: 'H2',         wrap: { before: '## ', after: '' },         block: true },
  { key: 'h3',       label: 'H3',         wrap: { before: '### ', after: '' },        block: true },
  { key: 'bold',     label: 'B',          wrap: { before: '**', after: '**' },         placeholder: 'bold' },
  { key: 'italic',   label: 'I',          wrap: { before: '_', after: '_' },           placeholder: 'italic' },
  { key: 'code',     label: '<>',         wrap: { before: '`', after: '`' },           placeholder: 'code' },
  { key: 'codeblock',label: '```',        wrap: { before: '\n```\n', after: '\n```\n' }, placeholder: 'code block' },
  { key: 'link',     label: 'Link',       wrap: { before: '[', after: '](https://)' }, placeholder: 'text' },
  { key: 'ul',       label: '• List',     wrap: { before: '- ', after: '' },           block: true },
  { key: 'ol',       label: '1. List',    wrap: { before: '1. ', after: '' },          block: true },
  { key: 'quote',    label: '> Quote',    wrap: { before: '> ', after: '' },           block: true },
  { key: 'hr',       label: 'HR',         insert: '\n\n---\n\n' },
];

export default function ReadmeEditor({ repo, readOnly = false }) {
  const [content, setContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [sha, setSha] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [viewMode, setViewMode] = useState('split'); // 'edit' | 'split' | 'preview'
  const textareaRef = useRef(null);

  const dirty = content !== savedContent;

  const fetchReadme = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSavedMsg(null);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/readme?repo=${encodeURIComponent(repo)}`, {
        method: 'GET',
        headers: { Authorization: idToken },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Failed (${response.status})`);
      }
      const data = await response.json();
      const incoming = data.content || '';
      setContent(incoming);
      setSavedContent(incoming);
      setSha(data.sha || null);
    } catch (err) {
      setError('Failed to load README: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  }, [repo]);

  useEffect(() => {
    if (repo) fetchReadme();
  }, [repo, fetchReadme]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    setConflict(null);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/readme?repo=${encodeURIComponent(repo)}`, {
        method: 'PUT',
        headers: { Authorization: idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, sha }),
      });
      if (response.status === 409) {
        const data = await response.json().catch(() => ({}));
        setConflict(data.error || 'README has changed since you loaded it.');
        return;
      }
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Failed (${response.status})`);
      }
      const data = await response.json();
      setSha(data.sha || null);
      setSavedContent(data.content ?? content);
      setSavedMsg('README saved.');
    } catch (err) {
      setError('Failed to save README: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    if (!dirty) return;
    if (!window.confirm('Discard unsaved changes?')) return;
    setContent(savedContent);
    setError(null);
    setSavedMsg(null);
    setConflict(null);
  };

  const applyToolbar = (btn) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = ta.value;

    let next;
    let cursorStart;
    let cursorEnd;

    if (btn.insert) {
      next = value.slice(0, start) + btn.insert + value.slice(end);
      cursorStart = cursorEnd = start + btn.insert.length;
    } else if (btn.block) {
      // Operate on whole lines covered by the selection.
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const lineEnd = end === start ? value.indexOf('\n', start) : end;
      const targetEnd = lineEnd === -1 ? value.length : lineEnd;
      const segment = value.slice(lineStart, targetEnd);
      const transformed = segment
        .split('\n')
        .map((line) => (line.startsWith(btn.wrap.before) ? line : btn.wrap.before + line))
        .join('\n');
      next = value.slice(0, lineStart) + transformed + value.slice(targetEnd);
      cursorStart = lineStart;
      cursorEnd = lineStart + transformed.length;
    } else {
      const selected = value.slice(start, end);
      const text = selected || btn.placeholder || '';
      const inserted = btn.wrap.before + text + btn.wrap.after;
      next = value.slice(0, start) + inserted + value.slice(end);
      cursorStart = start + btn.wrap.before.length;
      cursorEnd = cursorStart + text.length;
    }

    setContent(next);
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(cursorStart, cursorEnd);
      }
    });
  };

  const previewMarkdown = useMemo(() => content, [content]);

  if (loading) {
    return <div style={styles.loading}>Loading README…</div>;
  }

  return (
    <div style={styles.container} className="readme-editor">
      <style>{readmeEditorStyles}</style>
      <div style={styles.heading}>
        <div>
          <h2 style={styles.title}>README.md</h2>
          <p style={styles.subtitle}>
            Edits commit directly to <code>{repo}/README.md</code>. Use for VPN details and other
            site-specific notes that the next 16bit engineer needs.
          </p>
        </div>
        <div style={styles.viewToggle}>
          {['edit', 'split', 'preview'].map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              style={{
                ...styles.viewToggleBtn,
                ...(viewMode === mode ? styles.viewToggleBtnActive : {}),
              }}
            >
              {mode === 'edit' ? 'Edit' : mode === 'split' ? 'Split' : 'Preview'}
            </button>
          ))}
        </div>
      </div>

      {viewMode !== 'preview' && !readOnly && (
        <div style={styles.toolbar}>
          {TOOLBAR_BUTTONS.map((btn) => (
            <button
              key={btn.key}
              type="button"
              onClick={() => applyToolbar(btn)}
              style={styles.toolbarBtn}
              title={btn.key}
            >
              {btn.label}
            </button>
          ))}
        </div>
      )}

      <div style={{
        ...styles.editorRow,
        gridTemplateColumns:
          viewMode === 'split' ? '1fr 1fr' : '1fr',
      }}>
        {viewMode !== 'preview' && (
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            spellCheck={false}
            readOnly={readOnly}
            style={styles.textarea}
            placeholder="# Customer Notes&#10;&#10;## VPN&#10;Host: ...&#10;User: ...&#10;Password: ..."
          />
        )}
        {viewMode !== 'edit' && (
          <div style={styles.preview}>
            {previewMarkdown.trim() ? (
              <div style={styles.previewInner}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{previewMarkdown}</ReactMarkdown>
              </div>
            ) : (
              <div style={styles.previewEmpty}>Preview will render here.</div>
            )}
          </div>
        )}
      </div>

      {conflict && (
        <div style={styles.conflict}>
          <strong>Conflict:</strong> {conflict}{' '}
          <button type="button" onClick={fetchReadme} style={styles.linkBtn}>
            Reload latest
          </button>
        </div>
      )}
      {error && <div style={styles.error}>{error}</div>}
      {savedMsg && <div style={styles.success}>{savedMsg}</div>}

      {readOnly ? (
        <div style={{ ...styles.conflict, marginTop: '16px' }}>
          This repository is archived — the README is read-only.
        </div>
      ) : (
        <div style={styles.actions}>
          <button
            type="button"
            onClick={handleRevert}
            disabled={!dirty || saving}
            style={{
              ...styles.secondaryBtn,
              ...((!dirty || saving) ? styles.disabledBtn : {}),
            }}
          >
            Discard changes
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            style={{
              ...styles.primaryBtn,
              ...((!dirty || saving) ? styles.disabledBtn : {}),
            }}
          >
            {saving ? 'Saving…' : dirty ? 'Save README' : 'Saved'}
          </button>
        </div>
      )}
    </div>
  );
}

const readmeEditorStyles = `
  .readme-editor textarea {
    background-color: #fdfdfd !important;
    color: #2c3e50 !important;
    border: none !important;
    border-right: 1px solid #e1e5e9 !important;
    border-radius: 0 !important;
    padding: 16px !important;
    font-family: Consolas, Monaco, 'Courier New', monospace !important;
    font-size: 13px !important;
    line-height: 1.6 !important;
  }
  .readme-editor textarea::placeholder {
    color: #bdc3c7;
  }
  .readme-editor code {
    color: #2c3e50;
    background: #f1f3f5;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 0.92em;
  }
  .readme-editor pre code {
    display: block;
    padding: 12px 14px;
    background: #f8f9fa;
    border: 1px solid #e1e5e9;
    border-radius: 6px;
    overflow-x: auto;
    font-size: 0.88em;
  }
  .readme-editor table {
    border-collapse: collapse;
    margin: 10px 0;
  }
  .readme-editor th, .readme-editor td {
    border: 1px solid #e1e5e9;
    padding: 6px 10px;
    text-align: left;
  }
  .readme-editor blockquote {
    border-left: 3px solid #3498db;
    margin: 10px 0;
    padding: 4px 14px;
    color: #5d6d7e;
    background: #f8f9fa;
  }
  .readme-editor a { color: #3498db; }
  .readme-editor h1, .readme-editor h2, .readme-editor h3,
  .readme-editor h4, .readme-editor h5, .readme-editor h6 {
    color: #2c3e50;
  }
`;

const styles = {
  container: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e1e5e9',
  },
  heading: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '16px',
  },
  title: {
    color: '#2c3e50',
    fontSize: '20px',
    fontWeight: 600,
    margin: 0,
    paddingBottom: '6px',
    borderBottom: '2px solid #3498db',
    display: 'inline-block',
  },
  subtitle: {
    color: '#7f8c8d',
    fontSize: '13px',
    margin: '10px 0 0 0',
    maxWidth: '640px',
    lineHeight: 1.5,
  },
  viewToggle: {
    display: 'flex',
    border: '1px solid #e1e5e9',
    borderRadius: '8px',
    overflow: 'hidden',
    flexShrink: 0,
  },
  viewToggleBtn: {
    background: 'white',
    border: 'none',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#7f8c8d',
    cursor: 'pointer',
    borderRight: '1px solid #e1e5e9',
  },
  viewToggleBtnActive: {
    background: '#3498db',
    color: 'white',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    padding: '8px',
    background: '#f8f9fa',
    border: '1px solid #e1e5e9',
    borderRadius: '8px 8px 0 0',
    borderBottom: 'none',
    marginBottom: 0,
  },
  toolbarBtn: {
    background: 'white',
    border: '1px solid #e1e5e9',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '12px',
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    color: '#2c3e50',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s',
  },
  editorRow: {
    display: 'grid',
    gap: '0',
    border: '1px solid #e1e5e9',
    borderRadius: '0 0 8px 8px',
    overflow: 'hidden',
    minHeight: '480px',
  },
  textarea: {
    width: '100%',
    minHeight: '480px',
    padding: '16px',
    border: 'none',
    borderRight: '1px solid #e1e5e9',
    fontSize: '13px',
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    lineHeight: 1.6,
    color: '#2c3e50',
    background: '#fdfdfd',
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
  },
  preview: {
    minHeight: '480px',
    padding: '16px 20px',
    background: 'white',
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  previewInner: {
    color: '#2c3e50',
    fontSize: '14px',
    lineHeight: 1.6,
  },
  previewEmpty: {
    color: '#bdc3c7',
    fontStyle: 'italic',
    fontSize: '13px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '20px',
  },
  primaryBtn: {
    background: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
  },
  secondaryBtn: {
    background: '#ecf0f1',
    color: '#2c3e50',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 20px',
    fontWeight: 500,
    fontSize: '14px',
    cursor: 'pointer',
  },
  disabledBtn: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  linkBtn: {
    background: 'transparent',
    border: 'none',
    color: '#3498db',
    cursor: 'pointer',
    fontWeight: 600,
    textDecoration: 'underline',
    padding: 0,
  },
  loading: {
    background: 'white',
    borderRadius: '12px',
    padding: '40px',
    textAlign: 'center',
    color: '#7f8c8d',
    border: '1px solid #e1e5e9',
  },
  error: {
    color: '#e74c3c',
    background: '#fdf2f2',
    padding: '12px 16px',
    borderRadius: '8px',
    marginTop: '16px',
    border: '1px solid #fecaca',
    fontSize: '13px',
  },
  success: {
    background: '#d4edda',
    border: '1px solid #c3e6cb',
    color: '#155724',
    padding: '12px 16px',
    borderRadius: '8px',
    marginTop: '16px',
    fontSize: '13px',
  },
  conflict: {
    background: '#fff3cd',
    border: '1px solid #ffeaa7',
    color: '#856404',
    padding: '12px 16px',
    borderRadius: '8px',
    marginTop: '16px',
    fontSize: '13px',
  },
};
